import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../utils/helpers';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const PushNotificationToggle = ({ compact = false }) => {
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSupported(false);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API}/push/status`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSubscribed(data.subscribed);
      }
    } catch (e) {
      // Silently fail - user might not be authenticated
    }
    setLoading(false);
  };

  const subscribe = async () => {
    setLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Permiso de notificaciones denegado');
        setLoading(false);
        return;
      }

      const registration = await navigator.serviceWorker.ready;

      let vapidKey = VAPID_PUBLIC_KEY;
      if (!vapidKey) {
        try {
          const keyRes = await fetch(`${API}/push/vapid-key`);
          if (keyRes.ok) {
            const keyData = await keyRes.json();
            vapidKey = keyData.vapid_key;
          }
        } catch (e) {}
      }

      if (!vapidKey) {
        toast.error('Configuracion de notificaciones no disponible');
        setLoading(false);
        return;
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      const timezoneOffset = new Date().getTimezoneOffset();

      const res = await fetch(`${API}/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          timezone_offset: timezoneOffset
        })
      });

      if (res.ok) {
        setSubscribed(true);
        toast.success('Notificaciones activadas! Recibiras tu carta del dia a las 9 AM');
      } else if (res.status === 401) {
        toast.error('Inicia sesion para activar notificaciones');
      } else {
        toast.error('Error al activar notificaciones');
      }
    } catch (e) {
      console.error('Push subscription error:', e);
      toast.error('Error al activar notificaciones. Verifica que tu navegador soporte esta funcion.');
    }
    setLoading(false);
  };

  const unsubscribe = async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }

      await fetch(`${API}/push/unsubscribe`, {
        method: 'POST',
        credentials: 'include'
      });

      setSubscribed(false);
      toast.success('Notificaciones desactivadas');
    } catch (e) {
      console.error('Push unsubscribe error:', e);
      toast.error('Error al desactivar notificaciones');
    }
    setLoading(false);
  };

  if (!supported) {
    return null; // Don't show if not supported
  }

  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${compact ? '' : 'p-3 rounded-lg bg-white/5'}`}>
        <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
        {!compact && <span className="text-sm text-[var(--text-muted)]">Cargando...</span>}
      </div>
    );
  }

  if (compact) {
    return (
      <button
        onClick={subscribed ? unsubscribe : subscribe}
        className={`p-2 rounded-lg transition-all ${
          subscribed 
            ? 'bg-purple-400/10 text-purple-400 hover:bg-purple-600/30' 
            : 'bg-white/5 text-[var(--text-muted)] hover:bg-white/10'
        }`}
        title={subscribed ? 'Desactivar notificaciones' : 'Activar notificaciones'}
        data-testid="push-toggle-compact"
      >
        {subscribed ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
      </button>
    );
  }

  return (
    <div 
      className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-gold-300/8"
      data-testid="push-notification-toggle"
    >
      <div className="flex items-center gap-3">
        {subscribed ? (
          <Bell className="w-5 h-5 text-purple-400" />
        ) : (
          <BellOff className="w-5 h-5 text-[var(--text-muted)]" />
        )}
        <div>
          <p className="text-sm font-medium text-gray-200">
            {subscribed ? 'Notificaciones activas' : 'Notificaciones desactivadas'}
          </p>
          <p className="text-xs text-[var(--text-muted)]">
            {subscribed 
              ? 'Recibiras tu carta del dia a las 9 AM' 
              : 'Activa para recibir recordatorios diarios'}
          </p>
        </div>
      </div>
      <button
        onClick={subscribed ? unsubscribe : subscribe}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
          subscribed 
            ? 'bg-red-600/20 text-red-400 hover:bg-red-600/30' 
            : 'bg-purple-600 text-white hover:bg-purple-700'
        }`}
        data-testid="push-toggle-btn"
      >
        {subscribed ? 'Desactivar' : 'Activar'}
      </button>
    </div>
  );
};

export default PushNotificationToggle;
